import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useIsFocused } from 'expo-router/react-navigation';

export type RightPanelWidth = 'default' | 'wide';

interface IRightPanelSlot {
  content: ReactNode;
  width: RightPanelWidth;
}

interface RightPanelSlotContextType {
  slot: IRightPanelSlot;
  setSlot: (next: IRightPanelSlot | ((prev: IRightPanelSlot) => IRightPanelSlot)) => void;
}

const EMPTY_SLOT: IRightPanelSlot = { content: null, width: 'default' };

const RightPanelSlotContext = createContext<RightPanelSlotContextType | undefined>(undefined);

export function RightPanelSlotProvider({ children }: { children: ReactNode }) {
  const [slot, setSlotState] = useState<IRightPanelSlot>(EMPTY_SLOT);

  const setSlot = useCallback(
    (next: IRightPanelSlot | ((prev: IRightPanelSlot) => IRightPanelSlot)) => {
      setSlotState(next);
    },
    []
  );

  const value = useMemo(() => ({ slot, setSlot }), [slot, setSlot]);

  return <RightPanelSlotContext.Provider value={value}>{children}</RightPanelSlotContext.Provider>;
}

export function useRightPanelSlot(): IRightPanelSlot {
  const context = useContext(RightPanelSlotContext);
  return context?.slot ?? EMPTY_SLOT;
}

/**
 * Register content for the large-screen right panel while the current screen is focused.
 * Clears the slot on blur or unmount, but only if the content hasn't been replaced by another
 * screen in the meantime (to handle sibling focus transitions in any tree order).
 *
 * @param content React node to render inside the right panel, or null/undefined to hide it.
 * @param width Panel width: 'default' (as on the feeds) or 'wide' for content that needs more room.
 */
export function useRegisterRightPanelSlot(content: ReactNode, width: RightPanelWidth = 'default') {
  // Depend on the stable setter only: the context value changes whenever the slot does
  const setSlot = useContext(RightPanelSlotContext)?.setSlot;
  const isFocused = useIsFocused();
  const lastRegisteredRef = useRef<ReactNode>(null);

  useEffect(() => {
    if (!setSlot) return;
    if (!isFocused || !content) return;

    setSlot({ content, width });
    lastRegisteredRef.current = content;

    return () => {
      setSlot((prev) => (prev.content === lastRegisteredRef.current ? EMPTY_SLOT : prev));
    };
  }, [isFocused, content, width, setSlot]);
}
